import { useState, useEffect } from 'react';
import { isProduction } from '@src/utils';
import { userHasPermission } from '@src/actions/_utils';

export default function useForm(value, { onChange, meta, interceptor }) {
  const [form, setForm] = useState(value);

  const normalizeValue = val => {
    if (Array.isArray(val)) return val.map(valueObj => valueObj.value);
    if (typeof val === 'object') return val.value;
    return val;
  };

  const updateForm = (key, formSideEffects) => val => {
    let newForm = {
      ...form,
      [key]: normalizeValue(val),
    };

    // BotPromptChatIcon view returns value as object,
    // not required to be normalized.
    if (key === 'chatIconConfig') {
      newForm[key] = val;
    }

    if (interceptor) newForm = interceptor(key, val, newForm);

    /**
     * To change the fields in the form on changing a field other than the keys in an object returned by the formSideEffects.
     * formSideEffects is expected to be a function that returns a sideEffects Object with key value pair of form field to be changed for dynamic side effects.
     * formSideEffects passed as an object results in static side effects
     */
    let formSideEffectsObj;

    if (typeof formSideEffects === 'function')
      formSideEffectsObj = formSideEffects(val, form, meta);
    else if (typeof formSideEffects === 'object')
      formSideEffectsObj = formSideEffects;

    if (formSideEffectsObj)
      Object.entries(formSideEffectsObj).forEach(([key, value]) => {
        newForm[key] = normalizeValue(value);
      });

    setForm(newForm);
    onChange(newForm);
  };

  const getValueFromOptions = (val, options) => {
    const isArray = Array.isArray(val);
    const filteredOptions = options.filter(option =>
      isArray ? val.indexOf(option.value) > -1 : option.value === val
    );
    // val can also be of type object in that case return val or default object
    if (typeof val === 'object' && !isArray) return val || { value: '' };
    return isArray ? filteredOptions : filteredOptions[0] || { value: '' };
  };

  const getValue = (val, options) => {
    if (options) return getValueFromOptions(val, options);
    return val;
  };

  const getInputProps = (input, errors) => {
    const key = `${input.key}`;
    const inputOnChange = updateForm(input.key, input.formSideEffects);
    const inputProps =
      typeof input.props === 'function'
        ? input.props(form[input.key], inputOnChange, form, meta)
        : input.props || {};

    /*
    inputProps value can be boolean, we want to give it precedence over value at input.key
    so we check if it exists -> !undefined and !null, pass it to get value,
    if it doesn't exist pass value associated to input.key
    because
    if we use an OR operator to assign value in function params,
    when inputProps value exists and is boolean and false,
    preference is given to input.key which we want to avoid for boolean values.
    */
    const inputPropsValueExists =
      inputProps.value !== undefined && inputProps.value !== null;

    let inputValue = getValue(
      inputPropsValueExists ? inputProps.value : form[input.key],
      inputProps.options
    );

    if (
      input.key === 'activePartners' &&
      Array.isArray(inputValue) &&
      inputValue.length
    ) {
      inputValue = inputValue[0].value;
    }

    const error = errors && !!errors[input.key];
    const isDisabledOnProduction = isProduction() && !input.prodEdit;

    inputProps.disabled =
      inputProps.disabled ||
      isDisabledOnProduction ||
      (input.permission && !userHasPermission(input.permission));
    // const errorMessage = error ? errors[input.key].message : '';

    return {
      key,
      onChange: inputOnChange,
      label: input.label,
      id: input.key,
      error,
      ...inputProps,
      value: inputValue,
      //   errorMessage,
    };
  };

  useEffect(() => {
    setForm(value);
  }, [value]);

  return [form, getInputProps];
}
